import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react';

import { foundations } from '../generated/foundations';
import { semanticTokens } from '../generated/semantic';
import { recipes, slotRecipes } from './components';
import { globalCss, keyframes, animationStyles } from './styles';
import { wrapTokens, wrapSemanticColors, wrapSemanticRefs, wrapTextStyles } from './utils/to-v3-tokens';

/** Semantic colour families this design system defines, not Chakra: each is a full 25-975 alias scale over
 * a foundation hue, from `tokens/semantic/themeColors.json`. */
const CUSTOM_COLOR_FAMILIES = ['brand', 'neutral', 'success', 'info', 'warning', 'error', 'highlight'] as const;

type AnyColorToken = Record<string, unknown>;

/** The numeric step each `colorPalette` sub-token maps to, per Chakra's custom-palette convention. Stock
 * registers these only for its own hues, so without them a custom-family palette resolves to near-black. */
const COLOR_PALETTE_STEPS = {
  subtle: '100',
  muted: '200',
  emphasized: '300',
  focusRing: '500',
  border: '500',
  solid: '600',
  fg: '700',
} as const;

/** Adds the `colorPalette.{solid,fg,...}` sub-tokens to each custom family beside, never replacing, its step
 * scale. Light-mode only: no family declares `_dark` steps, so a dark value would be invented. */
function withColorPaletteSubTokens<T extends Record<string, AnyColorToken>>(colors: T): T {
  const out: Record<string, AnyColorToken> = { ...colors };
  for (const family of CUSTOM_COLOR_FAMILIES) {
    out[family] = {
      ...out[family],
      contrast: { value: '{colors.white}' },
      ...Object.fromEntries(
        Object.entries(COLOR_PALETTE_STEPS).map(([subToken, step]) => [
          subToken,
          { value: `{colors.${family}.${step}}` },
        ])
      ),
    };
  }
  return out as T;
}

/** Shadow names stock also registers as semantic tokens, which emit under conditional selectors and beat a
 * same-named raw token; re-registering them semantically lets `baseConfig` win on merge order. */
const STOCK_COLLIDING_SHADOWS = ['xs', 'sm', 'md', 'lg', 'xl', 'inner'] as const;

/** Lifts the colliding raw shadow values into the semantic layer verbatim; the raw registration stays, so
 * repo-only names (`base`, `2xs`, `outline`, ...) resolve unchanged. */
function withCollidingShadowOverrides<T extends Record<string, unknown>>(semanticShadows: T): T {
  const out: Record<string, unknown> = { ...semanticShadows };
  for (const name of STOCK_COLLIDING_SHADOWS) {
    out[name] = { value: foundations.shadows[name] };
  }
  return out as T;
}

/** Flat colour names that share a CSS var with a nested stock token (`border-muted` vs `border.muted`); stock's
 * `_light` emits under `:root, .light` and outranks our `base` under `:where(html, .chakra-theme)`. */
const STOCK_COLLIDING_COLORS = ['border-muted'] as const;

/** Restates each colliding name's light value as `_light`, so it emits under stock's selector where
 * `baseConfig`'s later value wins; `base` stays for every other condition. */
function withCollidingColorOverrides<T extends Record<string, AnyColorToken>>(colors: T): T {
  const out: Record<string, AnyColorToken> = { ...colors };
  for (const name of STOCK_COLLIDING_COLORS) {
    const value = out[name]?.value;
    if (value && typeof value === 'object' && 'base' in value) out[name] = { value: { ...value, _light: value.base } };
  }
  return out as T;
}

export const baseConfig = defineConfig({
  cssVarsPrefix: 'udl',
  globalCss,
  theme: {
    tokens: {
      colors: wrapTokens(foundations.colors),
      borders: wrapTokens(foundations.borders),
      borderWidths: wrapTokens(foundations.borderWidths),
      radii: wrapTokens(foundations.radii),
      shadows: wrapTokens(foundations.shadows),
      sizes: wrapTokens(foundations.sizes),
      // Chakra's token category is `spacing`; the generated foundations call it `space`.
      spacing: wrapTokens(foundations.space),
      fonts: wrapTokens(foundations.fonts),
      fontSizes: wrapTokens(foundations.fontSizes),
      fontWeights: wrapTokens(foundations.fontWeights),
      lineHeights: wrapTokens(foundations.lineHeights),
      letterSpacings: wrapTokens(foundations.letterSpacings),
    },
    semanticTokens: {
      colors: withCollidingColorOverrides(withColorPaletteSubTokens(wrapSemanticColors(semanticTokens.colors))),
      spacing: wrapSemanticRefs(semanticTokens.space.base, 'spacing'),
      fontWeights: wrapSemanticRefs(semanticTokens.fontWeights, 'fontWeights'),
      shadows: withCollidingShadowOverrides(wrapSemanticRefs(semanticTokens.shadows, 'shadows', ['none'])),
    },
    textStyles: wrapTextStyles(semanticTokens.textStyles.base),
    keyframes,
    animationStyles,
    recipes,
    slotRecipes,
  },
});

export const system = createSystem(defaultConfig, baseConfig);

/** Back-compat alias: many consumers import `chakraUiTheme` and pass it to the provider. */
export const chakraUiTheme = system;
